import React, { useCallback, useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Logger from "../../services/Logger";
import { validateFilledCoordinatesCellConflicts, validateWordPositionValues } from "./filledCoordinatesValidation";

const RESOLVE_TYPES = {
    duplicates: {
        title: "Resolve duplicate words",
        description: "Duplicate words found. Remove one copy at a time—one instance will be kept.",
        emptyMsg: "No duplicate words to remove (conflicts may have been resolved)."
    },
    cellConflicts: {
        title: "Resolve cell conflicts",
        description: "These words use cells that have conflicting values. Remove a word to resolve the conflict.",
        emptyMsg: "No words using conflict cells (conflicts may have been resolved)."
    },
    containedWords: {
        title: "Resolve contained words",
        description: "These words are fully covered by longer words. Remove a word to resolve the conflict.",
        emptyMsg: "No contained words to remove (conflicts may have been resolved)."
    },
    wordMismatches: {
        title: "Resolve word/position mismatch",
        description: "These words have position values that do not spell the word correctly. Remove each word and re-add it to fix.",
        emptyMsg: "No mismatch words to remove (conflicts may have been resolved)."
    }
};

function parseCoords(gridTpl) {
    const raw = gridTpl?.filledCoordinates ?? gridTpl?.filled_coordinates;
    if (!raw) return [];
    try {
        return typeof raw === "string" ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
    } catch {
        return [];
    }
}

function getWordsToResolveInternal(coords, resolveType) {
    if (!coords?.length) return { coords: [], wordsWithIndex: [] };

    if (resolveType === "duplicates") {
        const seenByWord = new Map();
        const duplicateEntryIndices = new Set();
        coords.forEach((entry, idx) => {
            const t = (entry.text || entry.word || "").trim().toLowerCase();
            if (!t) return;
            const count = (seenByWord.get(t) || 0) + 1;
            seenByWord.set(t, count);
            if (count > 1) duplicateEntryIndices.add(idx);
        });
        const indexSet = duplicateEntryIndices;
        const wordsWithIndex = coords
            .map((entry, indexInCoords) => ({ entry, indexInCoords }))
            .filter(({ indexInCoords }) => indexSet.has(indexInCoords));
        return { coords, wordsWithIndex };
    }

    if (resolveType === "cellConflicts") {
        const cellResult = validateFilledCoordinatesCellConflicts(coords);
        if (cellResult.valid || !cellResult.conflicts?.length) return { coords, wordsWithIndex: [] };
        const conflictKeys = new Set((cellResult.conflicts || []).map((c) => `${c.row},${c.col}`));
        const wordsWithIndex = coords
            .map((entry, indexInCoords) => ({ entry, indexInCoords }))
            .filter(({ entry }) => {
                const positions = entry.positions || [];
                return positions.some((p) => p.row != null && p.col != null && conflictKeys.has(`${p.row},${p.col}`));
            });
        return { coords, wordsWithIndex };
    }

    if (resolveType === "wordMismatches") {
        const wordResult = validateWordPositionValues(coords);
        const mismatches = wordResult?.mismatches ?? [];
        if (mismatches.length === 0) return { coords, wordsWithIndex: [] };
        const mismatchActuals = new Set(mismatches.map((m) => String(m.actual || "")));
        const wordsWithIndex = coords
            .map((entry, indexInCoords) => {
                const positions = entry.positions || [];
                const actual = positions.map((p) => (p.value != null ? String(p.value) : "")).join(",");
                return { entry, indexInCoords, actual };
            })
            .filter(({ actual }) => mismatchActuals.has(actual))
            .map(({ entry, indexInCoords }) => ({ entry, indexInCoords }));
        return { coords, wordsWithIndex };
    }

    if (resolveType === "containedWords") {
        const wordList = coords.map((w) => (w.text || "").trim()).filter(Boolean);
        const wordListLower = wordList.map((w) => w.toLowerCase());
        const containedPairs = [];
        for (let i = 0; i < wordListLower.length; i++) {
            for (let j = i + 1; j < wordListLower.length; j++) {
                const a = wordListLower[i];
                const b = wordListLower[j];
                if (a === b) continue;
                if (b.includes(a)) containedPairs.push({ short: wordList[i], long: wordList[j] });
                else if (a.includes(b)) containedPairs.push({ short: wordList[j], long: wordList[i] });
            }
        }
        if (containedPairs.length === 0) return { coords, wordsWithIndex: [] };
        const shortWordsLower = new Set(containedPairs.map((p) => (p.short || "").toLowerCase().trim()).filter(Boolean));
        const wordsWithIndex = coords
            .map((entry, indexInCoords) => ({ entry, indexInCoords }))
            .filter(({ entry }) => {
                const t = (entry.text || entry.word || "").trim().toLowerCase();
                return t && shortWordsLower.has(t);
            });
        return { coords, wordsWithIndex };
    }

    return { coords, wordsWithIndex: [] };
}

/**
 * Standalone dialog to resolve puzzle errors: duplicates, cell conflicts,
 * contained words, or word/position mismatches. Shown directly when user
 * clicks Resolve from ValidationDialog.
 */
export default function ResolvePuzzleDialog({
    open,
    onClose,
    gridTpl,
    resolveType = "duplicates",
    onRemoveWords,
    onRefresh,
    onShowSnackbar
}) {
    const [removingEntryIndex, setRemovingEntryIndex] = useState(null);

    const { coords, wordsWithIndex } = useMemo(() => {
        const c = parseCoords(gridTpl);
        return getWordsToResolveInternal(c, resolveType);
    }, [gridTpl, resolveType]);

    const config = RESOLVE_TYPES[resolveType] || RESOLVE_TYPES.duplicates;

    const handleRemoveWord = useCallback(
        async (entry, indexInCoords) => {
            if (typeof onRemoveWords !== "function") return;
            const updated = coords.filter((_, i) => i !== indexInCoords);
            setRemovingEntryIndex(indexInCoords);
            try {
                await onRemoveWords(updated);
                onShowSnackbar?.(`Word "${(entry.text || entry.word || "").trim()}" removed`, "success");
                onRefresh?.();
            } catch (err) {
                Logger.error("Error removing word:", err);
                onShowSnackbar?.(err?.message || "Failed to remove word", "error");
            } finally {
                setRemovingEntryIndex(null);
            }
        },
        [coords, onRemoveWords, onRefresh, onShowSnackbar]
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="resolve-puzzle-dialog-title">
            <DialogTitle id="resolve-puzzle-dialog-title">{config.title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {config.description}
                </Typography>
                {wordsWithIndex.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {config.emptyMsg}
                    </Typography>
                ) : (
                    <List dense disablePadding>
                        {wordsWithIndex.map(({ entry, indexInCoords }) => {
                            const label = (entry.text || entry.word || "").trim() || "(no text)";
                            const isRemoving = removingEntryIndex === indexInCoords;
                            return (
                                <ListItem key={indexInCoords} divider>
                                    <ListItemText primary={label} />
                                    <ListItemSecondaryAction>
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            startIcon={isRemoving ? <CircularProgress size={14} /> : <DeleteOutlineIcon />}
                                            onClick={() => handleRemoveWord(entry, indexInCoords)}
                                            disabled={isRemoving}
                                        >
                                            Remove
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
